import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { QuizTemplate, QuizTemplateInsert, QuizTemplateUpdate, QuizInsert } from '@/types/quiz';
import { toast } from 'sonner';

export const useQuizTemplates = (brandId?: string, category?: string, quizTypeId?: string) => {
  return useQuery({
    queryKey: ['quiz-templates', brandId, category, quizTypeId],
    queryFn: async () => {
      let query = supabase
        .from('os_quiz_templates')
        .select(`
          *,
          quiz_type:os_quiz_types(id, name, display_name)
        `)
        .order('created_at', { ascending: false });

      if (brandId) {
        query = query.or(`brand_id.eq.${brandId},is_global.eq.true`);
      }

      if (category) {
        query = query.eq('category', category);
      }

      if (quizTypeId) {
        query = query.eq('quiz_type_id', quizTypeId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as QuizTemplate[];
    },
  });
};

export const useQuizTemplate = (templateId?: string) => {
  return useQuery({
    queryKey: ['quiz-template', templateId],
    queryFn: async () => {
      if (!templateId) return null;

      const { data, error } = await supabase
        .from('os_quiz_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (error) throw error;
      return data as QuizTemplate;
    },
    enabled: !!templateId,
  });
};

export const useCreateQuizTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (template: QuizTemplateInsert) => {
      // Ensure user_id is set to the authenticated user to satisfy RLS
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) throw new Error('User not authenticated');

      const payload: QuizTemplateInsert = {
        ...template,
        user_id: user.id,
      };

      const { data, error } = await supabase
        .from('os_quiz_templates')
        .insert(payload)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quiz-templates'] });
      toast.success('Template created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create template: ${error.message}`);
    },
  });
};

export const useUpdateQuizTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: QuizTemplateUpdate }) => {
      const { data, error } = await supabase
        .from('os_quiz_templates')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quiz-templates'] });
      queryClient.invalidateQueries({ queryKey: ['quiz-template'] });
      toast.success('Template updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update template: ${error.message}`);
    },
  });
};

export const useDeleteQuizTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('os_quiz_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quiz-templates'] });
      toast.success('Template deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete template: ${error.message}`);
    },
  });
};

export const useDuplicateQuizTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: string) => {
      // Fetch the original template
      const { data: original, error: fetchError } = await supabase
        .from('os_quiz_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (fetchError) throw fetchError;

      // Create a copy without id, created_at, updated_at
      const { id, created_at, updated_at, ...templateData } = original;
      const duplicate = {
        ...templateData,
        name: `${original.name} (Copy)`,
      };

      const { data, error } = await supabase
        .from('os_quiz_templates')
        .insert(duplicate)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quiz-templates'] });
      toast.success('Template duplicated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to duplicate template: ${error.message}`);
    },
  });
};

export const useCreateQuizFromTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ templateId, quizData }: { templateId: string; quizData: Partial<QuizInsert> }) => {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Fetch the template
      const { data: template, error: fetchError } = await supabase
        .from('os_quiz_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (fetchError) throw fetchError;

      // Create quiz from template - derive style and type from template
      const newQuiz: QuizInsert = {
        brand_id: quizData.brand_id!,
        user_id: user.id,
        name: quizData.name || `${template.name} Quiz`,
        description: quizData.description || template.description,
        quiz_type_id: template.quiz_type_id,
        quiz_style_id: template.quiz_style_id,
        questions: template.questions,
        stepping_approach: template.stepping_approach,
        feedback_mode: template.feedback_mode,
        explanation_timing: template.explanation_timing,
        visual_config: template.visual_config,
        actions_after_submit: template.actions_after_submit,
        instructions: template.instructions,
        template_id: templateId,
      };

      const { data, error } = await supabase
        .from('os_quizzes')
        .insert(newQuiz)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quizzes'] });
      toast.success('Quiz created from template successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create quiz from template: ${error.message}`);
    },
  });
};
